// src/components/Products.jsx
/**
 * Products.jsx (FEDIS) - PREMIUM UI/UX + STOCK OPTIMISTIC UPDATE
 * ==============================================================
 * Logique inchangée:
 * - API endpoints identiques
 * - Validation Zod identique
 * - States identiques (search, sort, openId, editing, etc.)
 *
 * Fix UX:
 * - Stock affiché se met à jour INSTANTANÉMENT (optimistic UI)
 * - Puis reloadProducts() resynchronise avec la DB
 *
 * Props attendues:
 * - products: Array (vient de App.jsx / Zustand)
 * - reloadProducts: function async (défini dans App.jsx)
 */

import React, { useMemo, useState, useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { apiFetch } from '../api.js'
import { fmtMoney, cls } from '../utils/helpers.js'
import { VariantsPanel } from './products/VariantsPanel.jsx'

import { useFormValidation } from '../hooks/useFormValidation.js'
import {
  productCreateSchema,
  productUpdateSchema,
  stockAdjustmentSchema,
} from '../schemas/product.schema.js'

import { ProductsTable } from './products/ProductsTable.jsx'

/* -----------------------------
  Tiny UI helpers (no deps)
----------------------------- */
function TonePill({ children, tone = 'slate' }) {
  const tones = {
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
    indigo: 'bg-slate-50 text-slate-800 border-slate-100',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    amber: 'bg-amber-50 text-amber-800 border-amber-100',
    rose: 'bg-rose-50 text-rose-700 border-rose-100',
  }
  return (
    <span
      className={cls(
        'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold border',
        tones[tone] || tones.slate
      )}
    >
      {children}
    </span>
  )
}

function SectionTitle({ title, desc, right }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="text-sm font-extrabold text-slate-900">{title}</div>
        {desc ? <div className="text-xs text-slate-500 mt-1">{desc}</div> : null}
      </div>
      {right}
    </div>
  )
}

function QuickDeltaBtn({ label, onClick, disabled, tone = 'slate' }) {
  const tones = {
    slate: 'bg-white border-slate-200 hover:bg-slate-50 text-slate-900',
    indigo: 'bg-slate-900 border-slate-900 hover:bg-slate-950 text-white',
    rose: 'bg-rose-600 border-rose-600 hover:bg-rose-700 text-white',
  }
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cls(
        'px-3 py-2 rounded-xl text-sm font-black border transition',
        disabled ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed' : tones[tone]
      )}
    >
      {label}
    </button>
  )
}

export default function Products({ products = [], reloadProducts }) {
  /* =========================
   * États
   * ========================= */
  const [name, setName] = useState('')
  const [sku, setSku] = useState('')
  const [family, setFamily] = useState('')
  const [price, setPrice] = useState('')
  const [stock, setStock] = useState('')

  const [searchParams] = useSearchParams()
  const [search, setSearch] = useState('')
  const [familyFilter, setFamilyFilter] = useState('')
  const [onlyInStock, setOnlyInStock] = useState(false)
  const [lowStockFilter, setLowStockFilter] = useState(false)
  const [sortBy, setSortBy] = useState('name') // name | stock | value

  // Activer filtre stock faible depuis URL (?lowstock=1)
  useEffect(() => {
    if (searchParams.get('lowstock') === '1') setLowStockFilter(true)
  }, [])

  const [saving, setSaving] = useState(false)
  const [createError, setCreateError] = useState('')

  // Import CSV/Excel
  const [importResult, setImportResult] = useState(null) // { counts, errors, mode }
  const [importing, setImporting] = useState(false)
  const [importError, setImportError] = useState('')

  // View mode: grid ou table
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('products_view_mode') || 'grid')

  // édition: un seul produit ouvert à la fois
  const [openId, setOpenId] = useState(null)
  const [editing, setEditing] = useState({})
  const [editError, setEditError] = useState('')

  // Mouvements du produit ouvert
  const [movementsData, setMovementsData] = useState(null)
  const [movementsLoading, setMovementsLoading] = useState(false)
  useEffect(() => {
    if (!openId) { setMovementsData(null); return }
    setMovementsLoading(true)
    apiFetch(`/api/products/${openId}/movements`)
      .then(d => setMovementsData(d.rows || []))
      .catch(() => setMovementsData([]))
      .finally(() => setMovementsLoading(false))
  }, [openId])

  // ✅ Optimistic stock: { [id]: number }
  const [optimisticStock, setOptimisticStock] = useState({})

  // Multi-select
  const [selectedIds, setSelectedIds] = useState([])
  const [bulkDeleting, setBulkDeleting] = useState(false)

  // Validation Zod
  const createValidation = useFormValidation(productCreateSchema)
  const editValidation = useFormValidation(productUpdateSchema)
  const stockValidation = useFormValidation(stockAdjustmentSchema)

  function availableStockRaw(p) {
    return Number(p.stock_quantity ?? p.stock ?? 0)
  }
  function stockMin(p) {
    return Number(p.stock_min ?? 0)
  }
  function unitPrice(p) {
    return Number(p.unit_price ?? p.price ?? 0)
  }

  // ✅ Stock affiché: optimistic si existe, sinon stock réel
  const displayStock = useCallback((p) => {
    const id = p?.id
    if (!id) return availableStockRaw(p)
    if (Object.prototype.hasOwnProperty.call(optimisticStock, id)) {
      return Number(optimisticStock[id] ?? 0)
    }
    return availableStockRaw(p)
  }, [optimisticStock])

  function stockValue(p) {
    return displayStock(p) * unitPrice(p)
  }

  /* =========================
   * KPIs
   * ========================= */
  const kpis = useMemo(() => {
    const totalProducts = products.length
    const totalQty = products.reduce((s, p) => s + displayStock(p), 0)
    const totalValue = products.reduce((s, p) => s + (displayStock(p) * unitPrice(p)), 0)
    const lowCount = products.reduce((s, p) => (displayStock(p) <= stockMin(p) ? s + 1 : s), 0)
    return { totalProducts, totalQty, totalValue, lowCount }
  }, [products, displayStock])

  // Familles distinctes pour le filtre
  const families = useMemo(() => {
    const s = new Set(products.map(p => String(p.family || '').trim()).filter(Boolean))
    return Array.from(s).sort()
  }, [products])

  /* =========================
   * Filtre + tri
   * ========================= */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()

    const arr = products
      .filter((p) => {
        if (onlyInStock && displayStock(p) <= 0) return false
        if (lowStockFilter && !(displayStock(p) <= stockMin(p) && stockMin(p) > 0)) return false
        if (familyFilter && String(p.family || '') !== familyFilter) return false
        if (!q) return true
        const nm = String(p.name || '').toLowerCase()
        const sk = String(p.sku || '').toLowerCase()
        const fm = String(p.family || '').toLowerCase()
        return nm.includes(q) || sk.includes(q) || fm.includes(q)
      })
      .slice()

    arr.sort((a, b) => {
      if (sortBy === 'stock') return displayStock(b) - displayStock(a)
      if (sortBy === 'value') return stockValue(b) - stockValue(a)
      return String(a.name || '').localeCompare(String(b.name || ''))
    })

    return arr
  }, [products, search, onlyInStock, sortBy, displayStock])

  // Pagination
  const PAGE_SIZE = 50
  const [page, setPage] = useState(0)

  // Remettre à la page 0 quand les filtres changent
  useEffect(() => {
    setPage(0)
  }, [search, onlyInStock, lowStockFilter, familyFilter, sortBy])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const paginated = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)

  // Désélectionner les produits hors du filtre actuel
  useEffect(() => {
    if (selectedIds.length === 0) return
    const filteredSet = new Set(filtered.map(p => p.id))
    setSelectedIds(prev => prev.filter(id => filteredSet.has(id)))
  }, [filtered]) // eslint-disable-line react-hooks/exhaustive-deps

  /* =========================
   * Ajout produit
   * ========================= */
  async function addProduct() {
    setCreateError('')

    const formData = {
      sku: sku || null,
      name: name.trim(),
      family: family.trim() || null,
      unit_price: Number(price) || 0,
      stock_quantity: Number(stock) || 0,
    }

    const validation = createValidation.validate(formData)
    if (!validation.success) {
      setCreateError('Corrigez les erreurs de validation')
      return
    }

    setSaving(true)
    try {
      await apiFetch('/api/products', { method: 'POST', body: validation.data })

      setName('')
      setSku('')
      setFamily('')
      setPrice('')
      setStock('')
      createValidation.clearErrors()
      setCreateError('')
      await reloadProducts()
    } catch (e) {
      setCreateError(e?.message || 'Erreur création produit')
    } finally {
      setSaving(false)
    }
  }

  /* =========================
   * Edition produit
   * ========================= */
  function openEdit(p) {
    setOpenId(p.id)
    setEditing({
      unit_price: unitPrice(p),
      stock_min: stockMin(p),
      family: String(p.family || ''),
      stock_delta: 0,
      stock_reason: 'adjustment',
    })
    setEditError('')
    editValidation.clearErrors()
    stockValidation.clearErrors()
  }

  function closeEdit() {
    setOpenId(null)
    setEditing({})
    setEditError('')
    editValidation.clearErrors()
    stockValidation.clearErrors()
  }

  async function saveEdit(p) {
    if (!p?.id) return
    setEditError('')

    const formData = {
      unit_price: Number(editing.unit_price || 0),
      stock_min: Number(editing.stock_min || 0),
      family: editing.family?.trim() || null,
    }

    const validation = editValidation.validate(formData)
    if (!validation.success) {
      setEditError('Corrigez les erreurs de validation')
      return
    }

    setSaving(true)
    try {
      await apiFetch(`/api/products/${p.id}`, { method: 'PATCH', body: validation.data })
      await reloadProducts()
      closeEdit()
    } catch (e) {
      setEditError(e?.message || 'Erreur mise à jour')
    } finally {
      setSaving(false)
    }
  }

  /* =========================
   * Ajustement stock
   * ========================= */
  async function adjustStock(id, delta, reason = 'adjustment') {
    const d = Number(delta)
    if (!Number.isFinite(d) || d === 0) {
      console.warn('[adjustStock] Delta invalide:', delta)
      return
    }

    // ✅ Optimistic update immédiat
    setOptimisticStock((prev) => {
      const base = Object.prototype.hasOwnProperty.call(prev, id)
        ? Number(prev[id] ?? 0)
        : Number((products.find(x => x.id === id)?.stock_quantity ?? products.find(x => x.id === id)?.stock ?? 0) || 0)

      return { ...prev, [id]: base + d }
    })

    setSaving(true)
    try {
      await apiFetch(`/api/products/${id}/stock`, {
        method: 'PATCH',
        body: {
          delta: d,
          reason: String(reason || 'adjustment'),
          ref: 'UI',
        },
      })

      // ✅ Sync serveur
      await reloadProducts()

      // ✅ On enlève l’override optimistic (on revient à la vérité serveur)
      setOptimisticStock((prev) => {
        const copy = { ...prev }
        delete copy[id]
        return copy
      })
    } catch (e) {
      const errorMsg = e?.message || 'Erreur ajustement stock'
      console.error('[adjustStock] Erreur:', errorMsg, e)
      setEditError(errorMsg)

      // ❌ rollback optimistic si erreur
      setOptimisticStock((prev) => {
        const copy = { ...prev }
        delete copy[id]
        return copy
      })

      alert(`Erreur lors de l'ajustement du stock:\n${errorMsg}`)
    } finally {
      setSaving(false)
    }
  }

  async function applyStockDelta(p) {
    const formData = {
      delta: Number(editing.stock_delta || 0),
      reason: editing.stock_reason || 'adjustment',
    }

    const validation = stockValidation.validate(formData)
    if (!validation.success) {
      setEditError('Delta invalide (doit être ≠ 0)')
      return
    }

    await adjustStock(p.id, validation.data.delta, validation.data.reason)

    // garde la drawer ouverte, reset delta
    setEditing((ed) => ({ ...ed, stock_delta: 0 }))
    stockValidation.clearErrors()
  }

  /* =========================
   * Import CSV / Excel
   * ========================= */
  async function handleImport(file, mode = 'upsert') {
    if (!file) return
    setImporting(true)
    setImportError('')
    setImportResult(null)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const csrfToken = document.cookie.match(/csrf_token=([^;]+)/)?.[1] || ''
      const res = await fetch(`/api/products/import?mode=${mode}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-CSRF-Token': csrfToken },
        body: formData,
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data?.error || 'Erreur import')
      setImportResult({ ...data, mode })
      await reloadProducts()
    } catch (e) {
      setImportError(e?.message || 'Erreur import')
    } finally {
      setImporting(false)
    }
  }

  function exportCSV() {
    const header = 'SKU,Nom,Famille,Prix (XOF),Stock,Stock min,Valeur stock (XOF)\n'
    const rows = filtered.map(p =>
      [p.sku || '', p.name || '', p.family || '', unitPrice(p), displayStock(p), stockMin(p), (displayStock(p) * unitPrice(p))]
        .map(v => `"${String(v ?? '').replace(/"/g, '""')}"`)
        .join(',')
    ).join('\n')
    const blob = new Blob(['\uFEFF' + header + rows], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `produits-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  async function bulkDelete() {
    const toDelete = selectedIds.filter(id => {
      const p = products.find(x => x.id === id)
      return p && displayStock(p) === 0
    })
    const blocked = selectedIds.length - toDelete.length
    if (toDelete.length === 0) {
      alert('Aucun produit sélectionné n\'a stock = 0. Impossible de supprimer.')
      return
    }
    const ok = window.confirm(
      `Supprimer ${toDelete.length} produit(s) définitivement ?` +
      (blocked > 0 ? `\n(${blocked} ignoré(s) car stock > 0)` : '')
    )
    if (!ok) return
    setBulkDeleting(true)
    let errors = 0
    for (const id of toDelete) {
      try {
        await apiFetch(`/api/products/${id}`, { method: 'DELETE' })
      } catch { errors++ }
    }
    await reloadProducts()
    setSelectedIds([])
    setBulkDeleting(false)
    if (errors > 0) alert(`${errors} erreur(s) lors de la suppression.`)
  }

  function downloadTemplate() {
    const header = 'sku,name,family,unit_price,stock_quantity,stock_min\n'
    const example = 'SKU-001,Produit exemple,Catégorie A,5000,10,2\n,Produit sans SKU,Catégorie B,3000,5,1\n'
    const blob = new Blob(['\uFEFF' + header + example], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'modele_import_produits.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  /* =========================
   * Suppression
   * ========================= */
  async function deleteProduct(p) {
    if (!p?.id) return
    const stockVal = displayStock(p)
    if (stockVal > 0) {
      alert(`Impossible de supprimer "${p.name}" car stock = ${stockVal}. Mets stock à 0.`)
      return
    }

    const ok = window.confirm(
      `Supprimer définitivement ce produit ?\n\n` +
        `- Nom: ${p.name}\n- SKU: ${p.sku || '—'}\n\n` +
        `Cette action est irréversible.`
    )
    if (!ok) return

    setSaving(true)
    try {
      await apiFetch(`/api/products/${p.id}`, { method: 'DELETE' })
      await reloadProducts()
      closeEdit()
    } catch (e) {
      alert('Erreur suppression : ' + (e?.message || e))
    } finally {
      setSaving(false)
    }
  }

  /* =========================
   * UI classes
   * ========================= */
  const inputBase =
    'w-full border border-slate-200 bg-white rounded-xl px-3 py-2 text-sm outline-none transition focus:ring-2 focus:ring-slate-200 focus:border-slate-300'
  const inputError =
    'w-full border border-rose-300 bg-rose-50 rounded-xl px-3 py-2 text-sm outline-none transition focus:ring-2 focus:ring-rose-200 focus:border-rose-400'
  const cardBase =
    'bg-white border border-slate-200 rounded-3xl shadow-[0_6px_24px_-12px_rgba(15,23,42,0.18)]'

  const createErrors = createValidation.errors
  const editErrors = editValidation.errors
  const stockErrors = stockValidation.errors

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="p-6 max-w-6xl mx-auto space-y-6">
        {/* HEADER premium */}
        <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="p-6 sm:p-7">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">Produits</h1>
                  <TonePill tone="indigo">Catalogue</TonePill>
                  {saving ? <TonePill tone="slate">Sync…</TonePill> : null}
                </div>
                <p className="text-sm text-slate-600 mt-2 max-w-xl">
                  Gestion du catalogue, stock et prix. Ajustements instantanés + synchronisation serveur.
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 min-w-[260px]">
                <div className="text-xs text-slate-500 font-semibold">Valeur stock</div>
                <div className="text-2xl font-black text-slate-900 mt-1">{fmtMoney(kpis.totalValue)}</div>
                <div className="text-xs text-slate-600 mt-1">
                  <span className="font-semibold">{kpis.totalQty.toLocaleString('fr-FR')}</span> unités ·{' '}
                  <span className="font-semibold">{kpis.totalProducts}</span> produits
                </div>
              </div>
            </div>

            {/* KPIs */}
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-3xl border border-slate-200 bg-white p-4">
                <div className="text-xs text-slate-500 font-semibold">Produits</div>
                <div className="text-xl font-black text-slate-900 mt-1">{kpis.totalProducts}</div>
                <div className="text-[11px] text-slate-500 mt-1">Articles en catalogue</div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-4">
                <div className="text-xs text-slate-500 font-semibold">Stock total</div>
                <div className="text-xl font-black text-slate-900 mt-1">{kpis.totalQty.toLocaleString('fr-FR')}</div>
                <div className="text-[11px] text-slate-500 mt-1">Quantité disponible</div>
              </div>

              <button
                type="button"
                onClick={() => setLowStockFilter(v => !v)}
                className={cls(
                  'rounded-3xl border p-4 text-left transition',
                  lowStockFilter
                    ? 'border-amber-300 bg-amber-50 ring-2 ring-amber-200'
                    : 'border-slate-200 bg-white hover:border-amber-200'
                )}
              >
                <div className="text-xs text-slate-500 font-semibold">Faible stock</div>
                <div className={cls('text-xl font-black mt-1', kpis.lowCount ? 'text-amber-800' : 'text-slate-900')}>
                  {kpis.lowCount}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">{lowStockFilter ? 'Filtre actif ✓' : 'À surveiller'}</div>
              </button>

              <div className="rounded-3xl border border-slate-200 bg-white p-4">
                <div className="text-xs text-slate-500 font-semibold">Résultats</div>
                <div className="text-xl font-black text-slate-900 mt-1">{filtered.length}</div>
                <div className="text-[11px] text-slate-500 mt-1">Après filtres</div>
              </div>
            </div>
          </div>
        </div>

        {/* ACTIONS BAR */}
        <div className={cls(cardBase, 'p-5')}>
          <SectionTitle
            title="Recherche & filtres"
            desc="Trouvez vite, filtrez proprement, triez intelligemment."
            right={
              <div className="flex items-center gap-2">
                <button
                  onClick={exportCSV}
                  disabled={filtered.length === 0}
                  className={cls(
                    'px-3 py-2 rounded-xl text-sm font-bold border transition',
                    filtered.length === 0
                      ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                      : 'bg-white text-slate-900 border-slate-200 hover:bg-slate-50'
                  )}
                  title="Exporter la liste filtrée en CSV"
                >
                  CSV
                </button>
                <button
                  onClick={reloadProducts}
                  disabled={saving}
                  className={cls(
                    'px-4 py-2 rounded-xl text-sm font-bold border transition',
                    saving
                      ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                      : 'bg-white text-slate-900 border-slate-200 hover:bg-slate-50'
                  )}
                  title="Rafraîchir"
                >
                  {saving ? '…' : 'Rafraîchir'}
                </button>
              </div>
            }
          />

          <div className="mt-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-5">
              <div className="text-xs font-semibold text-slate-500">Recherche</div>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nom ou SKU…"
                className={inputBase}
              />
            </div>

            <div className="md:col-span-2">
              <div className="text-xs font-semibold text-slate-500">Famille</div>
              <select value={familyFilter} onChange={(e) => setFamilyFilter(e.target.value)} className={inputBase}>
                <option value="">Toutes</option>
                {families.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>

            <div className="md:col-span-2">
              <div className="text-xs font-semibold text-slate-500">Tri</div>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={inputBase}>
                <option value="name">Nom</option>
                <option value="stock">Stock (desc)</option>
                <option value="value">Valeur stock (desc)</option>
              </select>
            </div>

            <div className="md:col-span-3">
              <div className="text-xs font-semibold text-slate-500">Filtre</div>
              <button
                onClick={() => setOnlyInStock((v) => !v)}
                className={cls(
                  'w-full px-4 py-2 rounded-xl text-sm font-bold border transition',
                  onlyInStock
                    ? 'bg-slate-900 text-white border-slate-900 hover:bg-slate-950'
                    : 'bg-white text-slate-900 border-slate-200 hover:bg-slate-50'
                )}
              >
                {onlyInStock ? 'Stock > 0 (actif)' : 'Stock > 0'}
              </button>
            </div>

            <div className="md:col-span-2">
              <div className="text-xs font-semibold text-slate-500">Vue</div>
              <button
                onClick={() => {
                  const newMode = viewMode === 'grid' ? 'table' : 'grid'
                  setViewMode(newMode)
                  localStorage.setItem('products_view_mode', newMode)
                }}
                className={cls(
                  'w-full px-4 py-2 rounded-xl text-sm font-bold border transition',
                  viewMode === 'table'
                    ? 'bg-slate-900 text-white border-slate-900 hover:bg-slate-950'
                    : 'bg-white text-slate-900 border-slate-200 hover:bg-slate-50'
                )}
              >
                {viewMode === 'grid' ? '📊 Table' : '🔲 Grille'}
              </button>
            </div>
          </div>
        </div>

        {/* BULK ACTION BAR */}
        {selectedIds.length > 0 && (
          <div className="sticky top-4 z-10 rounded-2xl border border-slate-900 bg-slate-900 px-5 py-3 shadow-lg flex items-center gap-4 flex-wrap">
            <span className="text-sm font-bold text-white">{selectedIds.length} produit(s) sélectionné(s)</span>
            <button
              onClick={() => setSelectedIds(filtered.map(p => p.id))}
              className="text-xs text-slate-300 hover:text-white underline"
            >
              Tout sélectionner ({filtered.length})
            </button>
            <button
              onClick={() => setSelectedIds([])}
              className="text-xs text-slate-300 hover:text-white underline"
            >
              Désélectionner
            </button>
            <div className="flex-1" />
            <button
              onClick={bulkDelete}
              disabled={bulkDeleting}
              className={cls(
                'px-4 py-1.5 rounded-xl text-sm font-black transition',
                bulkDeleting
                  ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                  : 'bg-rose-600 text-white hover:bg-rose-700'
              )}
            >
              {bulkDeleting ? 'Suppression…' : 'Supprimer la sélection'}
            </button>
          </div>
        )}

        {/* AJOUT produit */}
        <div className={cls(cardBase, 'p-6')}>
          <SectionTitle
            title="Nouveau produit"
            desc="Création rapide avec validation."
            right={<TonePill tone="slate">Création</TonePill>}
          />

          {createError && (
            <div className="mt-4 p-3 border border-rose-200 rounded-2xl bg-rose-50 text-rose-700 text-sm font-semibold">
              {createError}
            </div>
          )}

          <div className="mt-4 grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-2">
              <div className="text-xs font-semibold text-slate-500">SKU</div>
              <input
                value={sku}
                onChange={(e) => { setSku(e.target.value); createValidation.clearFieldError('sku') }}
                className={createErrors.sku ? inputError : inputBase}
                placeholder="SKU"
              />
              {createErrors.sku && <div className="text-xs text-rose-600 mt-1">{createErrors.sku}</div>}
            </div>

            <div className="md:col-span-4">
              <div className="text-xs font-semibold text-slate-500">Nom *</div>
              <input
                value={name}
                onChange={(e) => {
                  setName(e.target.value)
                  createValidation.clearFieldError('name')
                }}
                className={createErrors.name ? inputError : inputBase}
                placeholder="Nom produit"
              />
              {createErrors.name && <div className="text-xs text-rose-600 mt-1">{createErrors.name}</div>}
            </div>

            <div className="md:col-span-2">
              <div className="text-xs font-semibold text-slate-500">Famille</div>
              <input
                value={family}
                onChange={(e) => setFamily(e.target.value)}
                className={inputBase}
                placeholder="Ex: Boissons"
                list="family-suggestions-create"
              />
              <datalist id="family-suggestions-create">
                {families.map(f => <option key={f} value={f} />)}
              </datalist>
            </div>

            <div className="md:col-span-2">
              <div className="text-xs font-semibold text-slate-500">Prix *</div>
              <input
                value={price}
                onChange={(e) => {
                  setPrice(e.target.value)
                  createValidation.clearFieldError('unit_price')
                }}
                type="number"
                min="0"
                className={createErrors.unit_price ? inputError : inputBase}
                placeholder="0"
              />
              {createErrors.unit_price && <div className="text-xs text-rose-600 mt-1">{createErrors.unit_price}</div>}
            </div>

            <div className="md:col-span-1">
              <div className="text-xs font-semibold text-slate-500">Stock initial</div>
              <input
                value={stock}
                onChange={(e) => {
                  setStock(e.target.value)
                  createValidation.clearFieldError('stock_quantity')
                }}
                type="number"
                className={createErrors.stock_quantity ? inputError : inputBase}
                placeholder="0"
              />
              {createErrors.stock_quantity && <div className="text-xs text-rose-600 mt-1">{createErrors.stock_quantity}</div>}
            </div>

            <div className="md:col-span-1 flex items-end">
              <button
                onClick={addProduct}
                disabled={saving}
                className={cls(
                  'w-full px-4 py-2 rounded-xl text-sm font-black text-white transition',
                  saving ? 'bg-slate-300 cursor-not-allowed' : 'bg-slate-900 hover:bg-slate-950 active:scale-[0.99]'
                )}
              >
                Ajouter
              </button>
            </div>
          </div>
        </div>

        {/* IMPORT CSV/Excel */}
        <div className={cls(cardBase, 'p-6')}>
          <SectionTitle
            title="Import produits CSV / Excel"
            desc="Importez ou mettez à jour votre catalogue en masse. Colonnes : sku, name, family, unit_price, stock_quantity, stock_min"
            right={
              <button
                onClick={downloadTemplate}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm text-slate-600 hover:bg-slate-50"
              >
                ↓ Modèle CSV
              </button>
            }
          />

          <div className="mt-4 flex flex-col sm:flex-row gap-3 items-start sm:items-end">
            <div>
              <div className="text-xs font-semibold text-slate-500 mb-1">Mode</div>
              <select
                id="import-mode"
                defaultValue="upsert"
                className={cls(inputBase, 'w-48')}
              >
                <option value="upsert">Upsert (maj + ajout)</option>
                <option value="replace">Remplacement total</option>
              </select>
            </div>

            <div>
              <div className="text-xs font-semibold text-slate-500 mb-1">Fichier</div>
              <label className={cls(
                'flex items-center gap-2 cursor-pointer px-4 py-2 rounded-xl text-sm font-semibold border transition',
                importing
                  ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                  : 'bg-slate-900 text-white border-slate-900 hover:bg-slate-950'
              )}>
                {importing ? '⏳ Import en cours…' : '📂 Choisir fichier'}
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  disabled={importing}
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (!file) return
                    const mode = document.getElementById('import-mode')?.value || 'upsert'
                    handleImport(file, mode)
                    e.target.value = ''
                  }}
                />
              </label>
            </div>
          </div>

          {importError && (
            <div className="mt-3 p-3 border border-rose-200 rounded-2xl bg-rose-50 text-rose-700 text-sm font-semibold">
              {importError}
            </div>
          )}

          {importResult && (
            <div className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
              <div className="text-sm font-black text-emerald-800">
                Import {importResult.mode === 'replace' ? 'remplacement' : 'upsert'} terminé
              </div>
              <div className="mt-1 text-sm text-emerald-700">
                {importResult.counts?.inserted ?? 0} ajouté(s) · {importResult.counts?.updated ?? 0} mis à jour · {importResult.counts?.skipped ?? 0} ignoré(s)
              </div>
              {importResult.errors?.length > 0 && (
                <div className="mt-2">
                  <div className="text-xs font-semibold text-rose-700">{importResult.errors.length} ligne(s) avec erreurs :</div>
                  <ul className="mt-1 text-xs text-rose-600 space-y-0.5 max-h-24 overflow-y-auto">
                    {importResult.errors.map((err, i) => (
                      <li key={i}>Ligne {err.row} : {err.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* VUE: Table ou Grille */}
        {viewMode === 'table' ? (
          <ProductsTable
            products={paginated}
            onRowClick={(id) => {
              if (openId === id) {
                closeEdit()
              } else {
                openEdit(filtered.find(p => p.id === id))
              }
            }}
            displayStock={displayStock}
            unitPrice={unitPrice}
            stockMin={stockMin}
            adjustStock={adjustStock}
            saving={saving}
            openId={openId}
            editing={editing}
            setEditing={setEditing}
            saveEdit={saveEdit}
            applyStockDelta={applyStockDelta}
            deleteProduct={deleteProduct}
            editError={editError}
            editErrors={editErrors}
            stockErrors={stockErrors}
            editValidation={editValidation}
            stockValidation={stockValidation}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {paginated.map((p) => {
            const st = displayStock(p)
            const min = stockMin(p)
            const low = st <= min && (min > 0 || st > 0)
            const zero = st === 0
            const opened = openId === p.id

            return (
              <div
                key={p.id}
                className={cls(
                  cardBase,
                  'p-6 transition-all',
                  opened
                    ? 'ring-2 ring-slate-200 border-slate-200'
                    : 'hover:shadow-[0_10px_30px_-18px_rgba(15,23,42,0.25)]',
                  zero ? 'opacity-90' : ''
                )}
              >
                {/* Top */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2 min-w-0">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(p.id)}
                      onChange={(e) => {
                        e.stopPropagation()
                        setSelectedIds(prev =>
                          e.target.checked ? [...prev, p.id] : prev.filter(id => id !== p.id)
                        )
                      }}
                      className="mt-1.5 shrink-0 rounded"
                      title="Sélectionner ce produit"
                    />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-lg font-black text-slate-900 truncate">{p.name}</div>
                        {low ? <TonePill tone="amber">Stock faible</TonePill> : null}
                        {zero ? <TonePill tone="rose">Rupture</TonePill> : null}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        SKU: <span className="text-slate-900 font-semibold">{p.sku || '—'}</span>
                        {p.family && <span className="ml-2">· <span className="text-slate-700 font-semibold">{p.family}</span></span>}
                      </div>
                    </div>
                  </div>

                  <div className="text-right shrink-0">
                    <div className="text-[11px] text-slate-500 font-semibold">Prix</div>
                    <div className="text-base font-black text-slate-900 tabular-nums">{fmtMoney(unitPrice(p))}</div>
                  </div>
                </div>

                {/* Stats */}
                <div className="mt-4 grid grid-cols-3 gap-3">
                  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3">
                    <div className="text-[11px] text-slate-500 font-semibold">Stock</div>
                    <div className={cls('text-lg font-black tabular-nums', zero ? 'text-rose-700' : 'text-slate-900')}>
                      {st}
                    </div>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3">
                    <div className="text-[11px] text-slate-500 font-semibold">Stock min</div>
                    <div className="text-lg font-black text-slate-900 tabular-nums">{min || 0}</div>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3">
                    <div className="text-[11px] text-slate-500 font-semibold">Valeur</div>
                    <div className="text-lg font-black text-slate-900 tabular-nums">{fmtMoney(stockValue(p))}</div>
                  </div>
                </div>

                {/* Actions rapides */}
                <div className="mt-4 flex flex-wrap gap-2">
                  <QuickDeltaBtn
                    label="+1"
                    tone="slate"
                    disabled={saving}
                    onClick={() => adjustStock(p.id, +1)}
                  />
                  <QuickDeltaBtn
                    label="-1"
                    tone="slate"
                    disabled={saving || st <= 0}
                    onClick={() => adjustStock(p.id, -1)}
                  />

                  {!opened ? (
                    <QuickDeltaBtn
                      label="Gérer"
                      tone="indigo"
                      disabled={saving}
                      onClick={() => openEdit(p)}
                    />
                  ) : (
                    <QuickDeltaBtn
                      label="Fermer"
                      tone="slate"
                      disabled={false}
                      onClick={closeEdit}
                    />
                  )}
                </div>

                {/* Drawer édition */}
                {opened && (
                  <div className="mt-5 border-t border-slate-200 pt-5 space-y-4">
                    {editError && (
                      <div className="p-3 border border-rose-200 rounded-2xl bg-rose-50 text-rose-700 text-sm font-semibold">
                        {editError}
                      </div>
                    )}

                    {/* Prix & seuil */}
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <SectionTitle title="Prix, seuil & famille" desc="Met à jour le prix, stock minimum et famille du produit." />
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <div className="text-xs font-semibold text-slate-500">Prix</div>
                          <input
                            type="number"
                            min="0"
                            className={editErrors.unit_price ? inputError : inputBase}
                            value={editing.unit_price}
                            onChange={(e) => {
                              setEditing((ed) => ({ ...ed, unit_price: Number(e.target.value) }))
                              editValidation.clearFieldError('unit_price')
                            }}
                          />
                          {editErrors.unit_price && <div className="text-xs text-rose-600 mt-1">{editErrors.unit_price}</div>}
                        </div>

                        <div>
                          <div className="text-xs font-semibold text-slate-500">Stock min</div>
                          <input
                            type="number"
                            min="0"
                            className={editErrors.stock_min ? inputError : inputBase}
                            value={editing.stock_min}
                            onChange={(e) => {
                              setEditing((ed) => ({ ...ed, stock_min: Number(e.target.value) }))
                              editValidation.clearFieldError('stock_min')
                            }}
                          />
                          {editErrors.stock_min && <div className="text-xs text-rose-600 mt-1">{editErrors.stock_min}</div>}
                        </div>

                        <div>
                          <div className="text-xs font-semibold text-slate-500">Famille</div>
                          <input
                            className={inputBase}
                            value={editing.family || ''}
                            onChange={(e) => setEditing((ed) => ({ ...ed, family: e.target.value }))}
                            placeholder="Ex: Boissons"
                            list="family-suggestions-edit"
                          />
                          <datalist id="family-suggestions-edit">
                            {families.map(f => <option key={f} value={f} />)}
                          </datalist>
                        </div>
                      </div>
                      <div className="mt-3 flex justify-end">
                        <button
                          onClick={() => saveEdit(p)}
                          disabled={saving}
                          className={cls(
                            'px-4 py-2 rounded-xl text-sm font-black text-white transition',
                            saving ? 'bg-slate-300 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700 active:scale-[0.99]'
                          )}
                        >
                          Sauver
                        </button>
                      </div>
                    </div>

                    {/* Ajuster stock (multi) */}
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <SectionTitle
                        title="Ajuster stock"
                        desc="Ajoute ou retire plusieurs unités d'un coup."
                      />

                      <div className="mt-3 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                        <div className="md:col-span-3">
                          <div className="text-xs font-semibold text-slate-500">Delta</div>
                          <input
                            type="number"
                            step="1"
                            className={stockErrors.delta ? inputError : inputBase}
                            value={editing.stock_delta}
                            onChange={(e) => {
                              setEditing((ed) => ({ ...ed, stock_delta: Number(e.target.value) }))
                              stockValidation.clearFieldError('delta')
                              setEditError('')
                            }}
                            placeholder="+10 / -3"
                          />
                          {stockErrors.delta && <div className="text-xs text-rose-600 mt-1">{stockErrors.delta}</div>}
                        </div>

                        <div className="md:col-span-6">
                          <div className="text-xs font-semibold text-slate-500">Motif</div>
                          <input
                            className={inputBase}
                            value={editing.stock_reason}
                            onChange={(e) => setEditing((ed) => ({ ...ed, stock_reason: e.target.value }))}
                            placeholder="inventaire, casse, correction…"
                          />
                        </div>

                        <div className="md:col-span-3">
                          <button
                            onClick={() => applyStockDelta(p)}
                            disabled={saving}
                            className={cls(
                              'w-full px-4 py-2 rounded-xl text-sm font-black text-white transition',
                              saving ? 'bg-slate-300 cursor-not-allowed' : 'bg-slate-900 hover:bg-slate-950 active:scale-[0.99]'
                            )}
                          >
                            Appliquer
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Variantes */}
                    <VariantsPanel productId={p.id} />

                    {/* Historique mouvements */}
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <SectionTitle title="Historique des mouvements" desc="50 derniers mouvements de stock." />
                      {movementsLoading ? (
                        <div className="text-xs text-slate-400 mt-2">Chargement…</div>
                      ) : !movementsData?.length ? (
                        <div className="text-xs text-slate-400 mt-2">Aucun mouvement enregistré.</div>
                      ) : (
                        <div className="mt-3 space-y-0 max-h-52 overflow-y-auto divide-y divide-slate-100">
                          {movementsData.map((m, i) => {
                            const REASON_LABELS = {
                              sale: 'Vente', purchase: 'Achat', purchase_receipt: 'Réception',
                              adjustment: 'Ajustement', manual_adjustment: 'Ajust. manuel',
                              return: 'Retour', inventory: 'Inventaire', cancelled: 'Annulé',
                            }
                            return (
                              <div key={m.id || i} className="flex items-center justify-between text-xs py-1.5">
                                <div className="flex items-center gap-2">
                                  <span className={cls('font-black w-10 text-right tabular-nums', m.qty >= 0 ? 'text-emerald-600' : 'text-rose-600')}>
                                    {m.qty >= 0 ? `+${m.qty}` : m.qty}
                                  </span>
                                  <span className="text-slate-700">{REASON_LABELS[m.reason] || m.reason}</span>
                                  {m.ref && <span className="text-slate-400 truncate max-w-[80px]">{m.ref}</span>}
                                </div>
                                <div className="text-slate-400 shrink-0 text-right">
                                  {m.created_by_username && <span className="mr-1 text-slate-500">{m.created_by_username}</span>}
                                  {new Date(m.created_at).toLocaleDateString('fr-FR')}
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>

                    {/* Suppression */}
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-slate-500">
                        Suppression possible seulement si <span className="font-semibold text-slate-900">stock = 0</span>.
                      </div>
                      <button
                        onClick={() => deleteProduct(p)}
                        disabled={saving || displayStock(p) > 0}
                        className={cls(
                          'px-4 py-2 rounded-xl text-sm font-black transition',
                          (saving || displayStock(p) > 0)
                            ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                            : 'bg-rose-600 text-white hover:bg-rose-700'
                        )}
                        title={displayStock(p) > 0 ? 'Mets le stock à 0 avant suppression' : 'Supprimer définitivement'}
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
          })}

            {filtered.length === 0 && (
              <div className={cls(cardBase, 'p-10 text-center text-slate-600 md:col-span-2')}>
                <div className="text-3xl">🧾</div>
                <div className="mt-2 font-extrabold text-slate-900">Aucun produit trouvé</div>
                <div className="mt-1 text-sm text-slate-600">Essaie une autre recherche ou enlève les filtres.</div>
              </div>
            )}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3">
            <div className="text-xs text-slate-500">
              {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} sur <span className="font-semibold text-slate-900">{filtered.length}</span> produits
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(0)}
                disabled={page === 0}
                className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-medium text-slate-700 disabled:opacity-40 hover:bg-slate-50 transition-colors"
              >
                «
              </button>
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-medium text-slate-700 disabled:opacity-40 hover:bg-slate-50 transition-colors"
              >
                Précédent
              </button>
              <span className="px-3 py-1 text-xs text-slate-600">
                Page <span className="font-semibold text-slate-900">{page + 1}</span> / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-medium text-slate-700 disabled:opacity-40 hover:bg-slate-50 transition-colors"
              >
                Suivant
              </button>
              <button
                onClick={() => setPage(totalPages - 1)}
                disabled={page >= totalPages - 1}
                className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-medium text-slate-700 disabled:opacity-40 hover:bg-slate-50 transition-colors"
              >
                »
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
